const MAX_BYTES = 15 * 1024 * 1024;
const MAX_PAGES = 60;

export class ContractReadError extends Error {}

const readAsText = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result));
  reader.onerror = () => reject(reader.error);
  reader.readAsText(file);
});

/** The text of a contract file, read in the browser: nothing about the file is sent to PayRight. */
export async function readContractText(file: File): Promise<string> {
  if (file.size > MAX_BYTES) throw new ContractReadError('That file is larger than 15 MB. Upload a smaller copy, or add the job by hand.');
  const isPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!isPdf) {
    if (file.type && !file.type.startsWith('text/')) throw new ContractReadError('Upload the contract as a PDF or a text file.');
    return readAsText(file);
  }
  // Loaded only when a contract is chosen, so the library is not part of every page.
  const [pdfjs, worker] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const document = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()), isEvalSupported: false }).promise;
  try {
    const pages: string[] = [];
    for (let number = 1; number <= Math.min(document.numPages, MAX_PAGES); number++) {
      const content = await (await document.getPage(number)).getTextContent();
      // Lines end where the text moves down the page, so "Label: value" stays on one line.
      let line = ''; let lastY: number | undefined;
      const lines: string[] = [];
      for (const item of content.items) {
        if (!('str' in item)) continue;
        const y = item.transform[5] as number;
        if (lastY !== undefined && Math.abs(y - lastY) > 2) { lines.push(line); line = ''; }
        line += item.str; lastY = y;
      }
      lines.push(line);
      pages.push(lines.join('\n'));
    }
    const text = pages.join('\n\n');
    if (text.replace(/\s/g, '').length < 40)
      throw new ContractReadError('This PDF has no text to read, which usually means it is a scan. Add the job by hand, or upload a text copy.');
    return text;
  } finally { await document.destroy(); }
}
